import styled from '@emotion/styled';

import type { RisingStarBonusCurrent, RisingStarBonusTier } from 'services/hooks/risingStarBonus';

import Flex from 'components/util/Flex';

import { useRisingStarCopy } from './copy';
import { formatAmount } from './format';
import { STitle } from './style';
import AmountValue from '../shared/AmountValue';

/**
 * 진행 바 위치(0~100). 금액이 아니라 표시 비율이라 FE 가 계산하는 유일한 값이다.
 * 구간 폭이 3천·5천·1만·2만·3만으로 불균등해 시안처럼 **균등 눈금**으로 둔다 — 0 과 각 구간 하한이 같은 간격.
 * 균등 눈금은 디자인 확인 3번 답변("시안대로", 10/6)으로 확정.
 */
const getProgress = (total: number, tiers: RisingStarBonusTier[]) => {
  const stops = [0, ...tiers.map((t) => t.threshold)];
  const last = stops.length - 1;
  // 구간 없음·음수·숫자 아님(필드 누락)이면 0 — 그대로 두면 width: NaN%
  if (!last || !(total > 0)) return 0;
  if (total >= stops[last]) return 100;

  const seg = stops.findIndex((s, i) => total >= s && total < stops[i + 1]);
  const frac = (total - stops[seg]) / (stops[seg + 1] - stops[seg]);

  return Math.round(((seg + frac) / last) * 100);
};

export default function TierProgress({ current, isBlur }: { current: RisingStarBonusCurrent; isBlur: boolean }) {
  const COPY = useRisingStarCopy();
  const { tiers, currentTier, nextTier } = current;
  const progress = getProgress(current.calculation.totalReward, tiers);
  // nextTier 는 최고 구간일 때와 산정 뒤(CALCULATED 이후)에 null 이다(BE 10/8 답변) — 최고 구간 문구는 실제 최고 구간일 때만
  const isTopTier = !!currentTier && currentTier.threshold === tiers[tiers.length - 1]?.threshold;

  return (
    <Flex direction="column">
      <STierTitle>{COPY.tierTitle}</STierTitle>
      <SBar
        role="progressbar"
        aria-label={COPY.tierTitle}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
      >
        <SFill style={{ width: `${progress}%` }} />
      </SBar>
      <STierList>
        {tiers.map((t) => {
          const isCurrent = currentTier?.threshold === t.threshold;

          return (
            <STierRow key={t.threshold} aria-current={isCurrent ? 'true' : undefined} $isCurrent={isCurrent}>
              <span>{COPY.tierRange(t.threshold)}</span>
              <span>{COPY.tierBonus(t.bonus)}</span>
            </STierRow>
          );
        })}
      </STierList>
      {nextTier ? (
        <SGuide>
          {/* 남은 금액도 금액이라 미인증이면 가린다 */}
          {COPY.nextTier(
            formatAmount(nextTier.threshold),
            isBlur ? '-' : formatAmount(nextTier.remaining),
            COPY.tierBonus(nextTier.bonus)
          )}
        </SGuide>
      ) : (
        // 미지급 달엔 '보너스로 적용돼요' 가 사실이 아니다 — 최고 구간이어도 안내하지 않는다
        isTopTier &&
        current.status !== 'CANCELLED' && (
          <SGuide>
            {COPY.topTier} <AmountValue amountFormat={formatAmount(current.amount)} isBlur={isBlur} />
          </SGuide>
        )
      )}
    </Flex>
  );
}

const STierTitle = styled(STitle)`
  margin-bottom: 12px;
`;

const SBar = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.color.bg.secondary};
  overflow: hidden;
`;

const SFill = styled.div`
  height: 100%;
  background-color: ${({ theme }) => theme.color.brand.primary};
`;

const STierList = styled.ul`
  padding: 0;
  list-style: none;
`;

const STierRow = styled.li<{ $isCurrent: boolean }>`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  padding: 8px 12px;
  border: 1px solid ${({ theme, $isCurrent }) => ($isCurrent ? theme.color.brand.primary : theme.color.border.subtle)};
  border-radius: 8px;
  background-color: ${({ theme, $isCurrent }) => ($isCurrent ? theme.color.highlight.primary['10'] : 'transparent')};
  color: ${({ theme, $isCurrent }) => ($isCurrent ? theme.color.label.primary : theme.color.label.secondary)};
  ${({ theme }) => theme.font.body2_m};
`;

const SGuide = styled.p`
  margin-top: 12px;
  color: ${({ theme }) => theme.color.label.secondary};
  ${({ theme }) => theme.font.caption1_m};
`;
